import { useState } from 'react';

export interface FilterSettings {
  brightness: number; // -100 to 100
  contrast: number; // -100 to 100
  saturation: number; // -100 to 100
  hue: number; // 0 to 360
  sharpness: number; // 0 to 100
  blur: number; // 0 to 20
  temperature: number; // -100 to 100
  tint: number; // -100 to 100
  exposure: number; // -100 to 100
  highlights: number; // -100 to 100
  shadows: number; // -100 to 100
  vignette: number; // 0 to 100
  grain: number; // 0 to 100
  preset?: 'none' | 'bw' | 'sepia' | 'vintage' | 'cinematic' | 'vibrant' | 'faded' | 'high-contrast';
}

interface FilteredImage {
  id: string;
  original: string;
  filtered: string;
  settings: FilterSettings;
}

export const DEFAULT_FILTERS: FilterSettings = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  hue: 0,
  sharpness: 0,
  blur: 0,
  temperature: 0,
  tint: 0,
  exposure: 0,
  highlights: 0,
  shadows: 0,
  vignette: 0,
  grain: 0,
  preset: 'none',
};

const PRESET_FILTERS: Record<string, Partial<FilterSettings>> = {
  bw: { saturation: -100, contrast: 20 },
  sepia: { saturation: -30, temperature: 30, tint: -20, contrast: -10 },
  vintage: { saturation: -20, temperature: 20, contrast: 15, vignette: 30, grain: 20 },
  cinematic: { saturation: -10, contrast: 25, shadows: -15, highlights: -10, vignette: 20 },
  vibrant: { saturation: 40, contrast: 15, sharpness: 20 },
  faded: { saturation: -30, contrast: -20, brightness: 10, highlights: 20 },
  'high-contrast': { contrast: 50, saturation: 20, sharpness: 15 },
};

export const useImageFilters = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filteredImages, setFilteredImages] = useState<FilteredImage[]>([]);

  const applyFilters = async (
    imageUrl: string,
    imageId: string,
    settings: FilterSettings
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;

          canvas.width = img.width;
          canvas.height = img.height;

          // Apply preset if specified
          let finalSettings = { ...settings };
          if (settings.preset && settings.preset !== 'none') {
            finalSettings = { ...settings, ...PRESET_FILTERS[settings.preset] };
          }

          // Draw image
          ctx.filter = buildFilterString(finalSettings);
          ctx.drawImage(img, 0, 0);

          // Apply advanced effects that need pixel manipulation
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          
          // Apply temperature and tint
          if (finalSettings.temperature !== 0 || finalSettings.tint !== 0) {
            applyColorTemperature(imageData, finalSettings.temperature, finalSettings.tint);
          }

          // Apply exposure, highlights, shadows
          if (finalSettings.exposure !== 0 || finalSettings.highlights !== 0 || finalSettings.shadows !== 0) {
            applyExposureAdjustments(imageData, finalSettings.exposure, finalSettings.highlights, finalSettings.shadows);
          }

          // Apply vignette
          if (finalSettings.vignette > 0) {
            applyVignette(imageData, finalSettings.vignette);
          }

          // Apply grain
          if (finalSettings.grain > 0) {
            applyGrain(imageData, finalSettings.grain);
          }

          ctx.putImageData(imageData, 0, 0);

          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const buildFilterString = (settings: FilterSettings): string => {
    const filters: string[] = [];

    if (settings.brightness !== 0) {
      filters.push(`brightness(${1 + settings.brightness / 100})`);
    }
    if (settings.contrast !== 0) {
      filters.push(`contrast(${1 + settings.contrast / 100})`);
    }
    if (settings.saturation !== 0) {
      filters.push(`saturate(${1 + settings.saturation / 100})`);
    }
    if (settings.hue !== 0) {
      filters.push(`hue-rotate(${settings.hue}deg)`);
    }
    if (settings.blur > 0) {
      filters.push(`blur(${settings.blur}px)`);
    }

    return filters.length > 0 ? filters.join(' ') : 'none';
  };

  const applyColorTemperature = (imageData: ImageData, temperature: number, tint: number) => {
    const data = imageData.data;
    const tempFactor = temperature / 100;
    const tintFactor = tint / 100;

    for (let i = 0; i < data.length; i += 4) {
      // Temperature: warm (orange) vs cool (blue)
      data[i] = Math.min(255, Math.max(0, data[i] + tempFactor * 30)); // Red
      data[i + 2] = Math.min(255, Math.max(0, data[i + 2] - tempFactor * 30)); // Blue

      // Tint: green vs magenta
      data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + tintFactor * 30)); // Green
    }
  };

  const applyExposureAdjustments = (
    imageData: ImageData,
    exposure: number,
    highlights: number,
    shadows: number
  ) => {
    const data = imageData.data;
    const exposureFactor = 1 + exposure / 100;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      // Calculate luminance
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

      // Apply exposure
      let newR = r * exposureFactor;
      let newG = g * exposureFactor;
      let newB = b * exposureFactor;

      // Apply highlights (affects bright areas)
      if (luminance > 128) {
        const highlightFactor = 1 + (highlights / 100) * ((luminance - 128) / 127);
        newR *= highlightFactor;
        newG *= highlightFactor;
        newB *= highlightFactor;
      }

      // Apply shadows (affects dark areas)
      if (luminance < 128) {
        const shadowFactor = 1 + (shadows / 100) * ((128 - luminance) / 128);
        newR *= shadowFactor;
        newG *= shadowFactor;
        newB *= shadowFactor;
      }

      data[i] = Math.min(255, Math.max(0, newR));
      data[i + 1] = Math.min(255, Math.max(0, newG));
      data[i + 2] = Math.min(255, Math.max(0, newB));
    }
  };

  const applyVignette = (imageData: ImageData, intensity: number) => {
    const { width, height, data } = imageData;
    const centerX = width / 2;
    const centerY = height / 2;
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const vignetteFactor = 1 - (distance / maxDistance) * (intensity / 100);

        const i = (y * width + x) * 4;
        data[i] *= vignetteFactor;
        data[i + 1] *= vignetteFactor;
        data[i + 2] *= vignetteFactor;
      }
    }
  };

  const applyGrain = (imageData: ImageData, intensity: number) => {
    const data = imageData.data;
    const grainAmount = intensity * 0.5;

    for (let i = 0; i < data.length; i += 4) {
      const noise = (Math.random() - 0.5) * grainAmount;
      data[i] = Math.min(255, Math.max(0, data[i] + noise));
      data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
      data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
    }
  };

  const processImages = async (
    images: Array<{ id: string; preview: string }>,
    settings: FilterSettings
  ) => {
    setIsProcessing(true);
    setProgress(0);
    const results: FilteredImage[] = [];

    for (let i = 0; i < images.length; i++) {
      try {
        const filtered = await applyFilters(images[i].preview, images[i].id, settings);
        results.push({
          id: images[i].id,
          original: images[i].preview,
          filtered,
          settings,
        });
      } catch (error) {
        console.error(`Failed to filter image ${images[i].id}:`, error);
      }
      setProgress(((i + 1) / images.length) * 100);
    }

    setFilteredImages(results);
    setIsProcessing(false);
    return results;
  };

  const downloadFiltered = (image: FilteredImage, filename: string) => {
    const link = document.createElement('a');
    link.href = image.filtered;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = (images: FilteredImage[], baseFilename: string) => {
    images.forEach((image, index) => {
      const filename = `${baseFilename}_filtered_${index + 1}.png`;
      downloadFiltered(image, filename);
    });
  };

  return {
    isProcessing,
    progress,
    filteredImages,
    processImages,
    downloadFiltered,
    downloadAll,
  };
};
